import fetchApiWithAuth from "./fetch";

export type MpDashboardMarketplace = "wildberries" | "ozon";

export type MpSyncDashboardSortField =
  | "article"
  | "remnant_1c"
  | "mp_stock"
  | "target_stock"
  | "last_sent_stock"
  | "remnant_1c_synced_at"
  | "mp_stock_synced_at"
  | "last_sent_at"
  | "sync_error_at";

export type MpSyncDiscrepancy =
  | "ok"
  | "blocked"
  | "quarantine"
  | "sync_error"
  | "no_mp_fact"
  | "mp_positive_1c_zero"
  | "mp_zero_1c_positive"
  | "mp_ne_target";

export type MpSyncActualityIssue =
  | "no_1c"
  | "stale_1c"
  | "no_mp_fact"
  | "stale_mp_fact"
  | "no_target"
  | "mismatch_blocked"
  | "mismatch_quarantine"
  | "mismatch_sync_error"
  | "mismatch_retirement"
  | "mismatch_pending_sync"
  | "mismatch_unknown";

export type MpSyncDashboardQuery = {
  marketplace?: MpDashboardMarketplace | "all";
  search?: string;
  page?: number;
  limit?: number;
  mp_positive_1c_zero?: boolean;
  mp_zero_1c_positive?: boolean;
  mp_gt_1c?: boolean;
  mp_ne_target?: boolean;
  quarantined?: boolean;
  blocked?: boolean;
  need_update?: boolean;
  with_sync_error?: boolean;
  not_actual?: boolean;
  stale_mp_not_zero_minutes?: number;
  sort_field?: MpSyncDashboardSortField;
  sort_order?: "ASC" | "DESC";
  remnant_1c_synced_from?: string;
  remnant_1c_synced_to?: string;
  mp_stock_synced_from?: string;
  mp_stock_synced_to?: string;
  last_sent_from?: string;
  last_sent_to?: string;
  sync_error_at_from?: string;
  sync_error_at_to?: string;
  remnant_1c_min?: number;
  remnant_1c_max?: number;
  mp_stock_min?: number;
  mp_stock_max?: number;
  last_sent_stock_min?: number;
  last_sent_stock_max?: number;
  discrepancy?: MpSyncDiscrepancy;
  brand?: string;
  category_id?: number;
};

export type MpSyncDashboardItem = {
  marketplace: MpDashboardMarketplace;
  article: string;
  name: string;
  identifier: string | null;
  remnant_1c: number | null;
  remnant_1c_synced_at: string | null;
  target_stock: number | null;
  mp_stock: number | null;
  mp_stock_synced_at: string | null;
  mp_reserved: number | null;
  last_sent_stock: number | null;
  last_sent_at: string | null;
  need_update: boolean;
  product_blocked: boolean;
  quarantine_at: string | null;
  quarantine_reason: string | null;
  sync_error: string | null;
  sync_error_at: string | null;
  retirement_state: string | null;
  share_formula: string | null;
  discrepancy: MpSyncDiscrepancy;
  is_actual: boolean;
  actuality_issues: MpSyncActualityIssue[];
  actuality_reason: string;
  stock_management_enabled: boolean;
  remnants_sending_enabled: boolean;
};

export type MpSyncDashboardResponse = {
  total: number;
  total_page: number;
  page: number;
  limit: number;
  summary: Record<MpSyncDiscrepancy, number>;
  actuality_summary: { actual: number; not_actual: number };
  need_update_summary: { queued: number };
  data: MpSyncDashboardItem[];
};

const buildQuery = (query?: MpSyncDashboardQuery) => {
  const params = new URLSearchParams();
  const setBool = (key: keyof MpSyncDashboardQuery) => {
    if (query?.[key]) params.append(key, "true");
  };
  const setVal = (key: keyof MpSyncDashboardQuery) => {
    const v = query?.[key];
    if (v != null && v !== "") params.append(key, String(v));
  };
  setVal("marketplace");
  setVal("search");
  setVal("page");
  setVal("limit");
  setBool("mp_positive_1c_zero");
  setBool("mp_zero_1c_positive");
  setBool("mp_gt_1c");
  setBool("mp_ne_target");
  setBool("quarantined");
  setBool("blocked");
  setBool("need_update");
  setBool("with_sync_error");
  setBool("not_actual");
  setVal("stale_mp_not_zero_minutes");
  setVal("sort_field");
  setVal("sort_order");
  setVal("remnant_1c_synced_from");
  setVal("remnant_1c_synced_to");
  setVal("mp_stock_synced_from");
  setVal("mp_stock_synced_to");
  setVal("last_sent_from");
  setVal("last_sent_to");
  setVal("sync_error_at_from");
  setVal("sync_error_at_to");
  setVal("remnant_1c_min");
  setVal("remnant_1c_max");
  setVal("mp_stock_min");
  setVal("mp_stock_max");
  setVal("last_sent_stock_min");
  setVal("last_sent_stock_max");
  setVal("discrepancy");
  setVal("brand");
  setVal("category_id");
  return params.toString();
};

export const getMpSyncDashboard = async (
  query?: MpSyncDashboardQuery
): Promise<MpSyncDashboardResponse> => {
  const qs = buildQuery(query);
  const url = `/api/goods/mp_sync_dashboard${qs ? `?${qs}` : ""}`;
  const response = await fetchApiWithAuth(url, { method: "GET" });
  return (await response.json()) as MpSyncDashboardResponse;
};

export const downloadMpSyncDashboardCsv = async (
  query?: MpSyncDashboardQuery
) => {
  const qs = buildQuery(query);
  const url = `/api/goods/mp_sync_dashboard/csv${qs ? `?${qs}` : ""}`;
  return fetchApiWithAuth(url, { method: "GET" });
};
